import type { TFunction } from "i18next";

export type SidebarNavItemId =
	| "chat"
	| "projects"
	| "group"
	| "image"
	| "video"
	| "audio"
	| "canvas";

export type SidebarNavIcon =
	| "message-square"
	| "folder"
	| "users"
	| "image"
	| "film"
	| "audio-lines"
	| "pen-tool";

export interface SidebarNavItem {
	id: SidebarNavItemId;
	label: string;
	tooltip: string;
	route: string;
	icon: SidebarNavIcon;
	badge?: string;
}

interface SidebarLabelMap {
	brand: string;
	signInRequired: string;
	signIn: string;
	createAccount: string;
	dashboard: string;
	theme: string;
	logOut: string;
	user: string;
	chats: string;
	pinned: string;
	noChatHistory: string;
	startConversation: string;
	loadingChats: string;
	signInForChatSidebar: string;
	signInForOrgSidebar: string;
	signInForProjectsSidebar: string;
	signInForSkillsSidebar: string;
	signInForImageSidebar: string;
	signInForVideoSidebar: string;
	signInForAudioSidebar: string;
	signInForCanvasSidebar: string;
	shareChatHint: string;
	generateImageHint: string;
	generateVideoHint: string;
	generateAudioHint: string;
	loadingProjects: string;
	noProjectsYet: string;
	fileSingular: string;
	filePlural: string;
	chatSingular: string;
	chatPlural: string;
	loadingSkills: string;
	noSkillsYet: string;
	writeSkill: string;
	generateWithAi: string;
	uploadSkill: string;
	searchChats: string;
	newChat: string;
	newGeneration: string;
	newCanvas: string;
	newProject: string;
	newSkill: string;
	myProjects: string;
	mySkills: string;
	noGenerationHistory: string;
	noSharedChats: string;
	timeGroups: {
		today: string;
		yesterday: string;
		last7Days: string;
		older: string;
	};
}

type Translate = TFunction;

export const NAV_ITEMS: readonly SidebarNavItem[] = [
	{
		id: "chat",
		label: "Chat",
		tooltip: "Chat",
		route: "/",
		icon: "message-square",
		badge: "Chat",
	},
	{
		id: "projects",
		label: "Projects",
		tooltip: "Projects",
		route: "/projects",
		icon: "folder",
	},
	{
		id: "group",
		label: "Group Chat",
		tooltip: "Group Chat",
		route: "/group",
		icon: "users",
	},
	{
		id: "image",
		label: "Image Studio",
		tooltip: "Image Studio",
		route: "/image",
		icon: "image",
		badge: "Image",
	},
	{
		id: "video",
		label: "Video Studio",
		tooltip: "Video Studio",
		route: "/video",
		icon: "film",
		badge: "Video",
	},
	{
		id: "audio",
		label: "Audio Studio",
		tooltip: "Audio Studio",
		route: "/audio",
		icon: "audio-lines",
		badge: "Audio",
	},
	{
		id: "canvas",
		label: "Canvas",
		tooltip: "Canvas",
		route: "/canvas",
		icon: "pen-tool",
		badge: "Canvas",
	},
] as const;

const navItemById = Object.fromEntries(
	NAV_ITEMS.map((item) => [item.id, item]),
) as Record<SidebarNavItemId, (typeof NAV_ITEMS)[number]>;

export const ALL_NAV_ITEM_IDS = [
	"chat",
	"projects",
	"group",
	"image",
	"video",
	"audio",
	"canvas",
] as const satisfies readonly SidebarNavItemId[];

export const CHAT_NAV_ITEM_IDS = ALL_NAV_ITEM_IDS;

export const DEFAULT_NAV_ITEM_IDS = [
	"chat",
	"projects",
	"group",
	"image",
	"video",
	"audio",
	"canvas",
] as const satisfies readonly SidebarNavItemId[];

export function getSidebarNavItems(
	ids: readonly SidebarNavItemId[],
): SidebarNavItem[] {
	return ids.map((id) => navItemById[id]);
}

export function filterEnabledNavItemIds(
	ids: readonly SidebarNavItemId[],
	enabledNavItems: readonly SidebarNavItemId[],
): SidebarNavItemId[] {
	const enabled = new Set<SidebarNavItemId>(enabledNavItems);
	return ids.filter((id) => enabled.has(id));
}

function localize(
	translate?: Translate,
	key?: string,
	fallback?: string,
): string {
	if (!translate || !key || !fallback) {
		return fallback ?? "";
	}
	const translated = translate(key, { defaultValue: fallback });
	return translated || fallback;
}

export function getSidebarNavItemsLocalized(
	ids: readonly SidebarNavItemId[],
	translate?: Translate,
): SidebarNavItem[] {
	return ids.map((id) => {
		const item = navItemById[id];
		return {
			...item,
			label: localize(translate, `sidebar.nav.${id}`, item.label),
			tooltip: localize(translate, `sidebar.nav.${id}`, item.tooltip),
			badge: item.badge
				? localize(translate, `sidebar.nav.${id}`, item.badge)
				: undefined,
		};
	});
}

export const SIDEBAR_LABELS = {
	brand: "ChatbotGPT",
	signInRequired: "Sign in required",
	signIn: "Sign in",
	createAccount: "Create account",
	dashboard: "Dashboard",
	theme: "Theme",
	logOut: "Log out",
	user: "User",
	chats: "Chats",
	pinned: "Pinned",
	noChatHistory: "No chat history",
	startConversation: "Start a new conversation to see it here",
	loadingChats: "Loading chats...",
	signInForChatSidebar:
		"Please sign in to view organizations, projects, and chats.",
	signInForOrgSidebar: "Please sign in to view organizations and shared chats.",
	signInForProjectsSidebar: "Please sign in to view your projects.",
	signInForSkillsSidebar: "Please sign in to view your skills.",
	signInForImageSidebar: "Please sign in to generate images.",
	signInForVideoSidebar: "Please sign in to generate videos.",
	signInForAudioSidebar: "Please sign in to generate audio.",
	signInForCanvasSidebar: "Please sign in to use Canvas.",
	shareChatHint: "Share a chat with this organization to see it here",
	generateImageHint: "Generate an image to see it here",
	generateVideoHint: "Generate a video to see it here",
	generateAudioHint: "Generate audio to see it here",
	loadingProjects: "Loading projects...",
	noProjectsYet: "No projects yet",
	fileSingular: "file",
	filePlural: "files",
	chatSingular: "chat",
	chatPlural: "chats",
	loadingSkills: "Loading skills...",
	noSkillsYet: "No skills yet",
	writeSkill: "Write skill",
	generateWithAi: "Generate with AI",
	uploadSkill: "Upload a skill",
	searchChats: "Search Chats",
	newChat: "New Chat",
	newGeneration: "New Generation",
	newCanvas: "New Canvas",
	newProject: "New Project",
	newSkill: "New Skill",
	myProjects: "My Projects",
	mySkills: "My Skills",
	noGenerationHistory: "No generation history",
	noSharedChats: "No shared chats",
	timeGroups: {
		today: "Today",
		yesterday: "Yesterday",
		last7Days: "Last 7 days",
		older: "Older",
	},
} as const;

export function getSidebarLabels(translate?: Translate): SidebarLabelMap {
	return {
		brand: localize(translate, "sidebar.brand", SIDEBAR_LABELS.brand),
		signInRequired: localize(
			translate,
			"sidebar.signInRequired",
			SIDEBAR_LABELS.signInRequired,
		),
		signIn: localize(translate, "sidebar.signIn", SIDEBAR_LABELS.signIn),
		createAccount: localize(
			translate,
			"sidebar.createAccount",
			SIDEBAR_LABELS.createAccount,
		),
		dashboard: localize(
			translate,
			"sidebar.dashboard",
			SIDEBAR_LABELS.dashboard,
		),
		theme: localize(translate, "sidebar.theme", SIDEBAR_LABELS.theme),
		logOut: localize(translate, "sidebar.logOut", SIDEBAR_LABELS.logOut),
		user: localize(translate, "sidebar.user", SIDEBAR_LABELS.user),
		chats: localize(translate, "sidebar.chats", SIDEBAR_LABELS.chats),
		pinned: localize(translate, "sidebar.pinned", SIDEBAR_LABELS.pinned),
		noChatHistory: localize(
			translate,
			"sidebar.noChatHistory",
			SIDEBAR_LABELS.noChatHistory,
		),
		startConversation: localize(
			translate,
			"sidebar.startConversation",
			SIDEBAR_LABELS.startConversation,
		),
		loadingChats: localize(
			translate,
			"sidebar.loadingChats",
			SIDEBAR_LABELS.loadingChats,
		),
		signInForChatSidebar: localize(
			translate,
			"sidebar.signInForChatSidebar",
			SIDEBAR_LABELS.signInForChatSidebar,
		),
		signInForOrgSidebar: localize(
			translate,
			"sidebar.signInForOrgSidebar",
			SIDEBAR_LABELS.signInForOrgSidebar,
		),
		signInForProjectsSidebar: localize(
			translate,
			"sidebar.signInForProjectsSidebar",
			SIDEBAR_LABELS.signInForProjectsSidebar,
		),
		signInForSkillsSidebar: localize(
			translate,
			"sidebar.signInForSkillsSidebar",
			SIDEBAR_LABELS.signInForSkillsSidebar,
		),
		signInForImageSidebar: localize(
			translate,
			"sidebar.signInForImageSidebar",
			SIDEBAR_LABELS.signInForImageSidebar,
		),
		signInForVideoSidebar: localize(
			translate,
			"sidebar.signInForVideoSidebar",
			SIDEBAR_LABELS.signInForVideoSidebar,
		),
		signInForAudioSidebar: localize(
			translate,
			"sidebar.signInForAudioSidebar",
			SIDEBAR_LABELS.signInForAudioSidebar,
		),
		signInForCanvasSidebar: localize(
			translate,
			"sidebar.signInForCanvasSidebar",
			SIDEBAR_LABELS.signInForCanvasSidebar,
		),
		shareChatHint: localize(
			translate,
			"sidebar.shareChatHint",
			SIDEBAR_LABELS.shareChatHint,
		),
		generateImageHint: localize(
			translate,
			"sidebar.generateImageHint",
			SIDEBAR_LABELS.generateImageHint,
		),
		generateVideoHint: localize(
			translate,
			"sidebar.generateVideoHint",
			SIDEBAR_LABELS.generateVideoHint,
		),
		generateAudioHint: localize(
			translate,
			"sidebar.generateAudioHint",
			SIDEBAR_LABELS.generateAudioHint,
		),
		loadingProjects: localize(
			translate,
			"sidebar.loadingProjects",
			SIDEBAR_LABELS.loadingProjects,
		),
		noProjectsYet: localize(
			translate,
			"sidebar.noProjectsYet",
			SIDEBAR_LABELS.noProjectsYet,
		),
		fileSingular: localize(
			translate,
			"sidebar.fileSingular",
			SIDEBAR_LABELS.fileSingular,
		),
		filePlural: localize(
			translate,
			"sidebar.filePlural",
			SIDEBAR_LABELS.filePlural,
		),
		chatSingular: localize(
			translate,
			"sidebar.chatSingular",
			SIDEBAR_LABELS.chatSingular,
		),
		chatPlural: localize(
			translate,
			"sidebar.chatPlural",
			SIDEBAR_LABELS.chatPlural,
		),
		loadingSkills: localize(
			translate,
			"sidebar.loadingSkills",
			SIDEBAR_LABELS.loadingSkills,
		),
		noSkillsYet: localize(
			translate,
			"sidebar.noSkillsYet",
			SIDEBAR_LABELS.noSkillsYet,
		),
		writeSkill: localize(
			translate,
			"sidebar.writeSkill",
			SIDEBAR_LABELS.writeSkill,
		),
		generateWithAi: localize(
			translate,
			"sidebar.generateWithAi",
			SIDEBAR_LABELS.generateWithAi,
		),
		uploadSkill: localize(
			translate,
			"sidebar.uploadSkill",
			SIDEBAR_LABELS.uploadSkill,
		),
		searchChats: localize(
			translate,
			"sidebar.searchChats",
			SIDEBAR_LABELS.searchChats,
		),
		newChat: localize(translate, "sidebar.newChat", SIDEBAR_LABELS.newChat),
		newGeneration: localize(
			translate,
			"sidebar.newGeneration",
			SIDEBAR_LABELS.newGeneration,
		),
		newCanvas: localize(
			translate,
			"sidebar.newCanvas",
			SIDEBAR_LABELS.newCanvas,
		),
		newProject: localize(
			translate,
			"sidebar.newProject",
			SIDEBAR_LABELS.newProject,
		),
		newSkill: localize(translate, "sidebar.newSkill", SIDEBAR_LABELS.newSkill),
		myProjects: localize(
			translate,
			"sidebar.myProjects",
			SIDEBAR_LABELS.myProjects,
		),
		mySkills: localize(translate, "sidebar.mySkills", SIDEBAR_LABELS.mySkills),
		noGenerationHistory: localize(
			translate,
			"sidebar.noGenerationHistory",
			SIDEBAR_LABELS.noGenerationHistory,
		),
		noSharedChats: localize(
			translate,
			"sidebar.noSharedChats",
			SIDEBAR_LABELS.noSharedChats,
		),
		timeGroups: {
			today: localize(
				translate,
				"sidebar.timeGroups.today",
				SIDEBAR_LABELS.timeGroups.today,
			),
			yesterday: localize(
				translate,
				"sidebar.timeGroups.yesterday",
				SIDEBAR_LABELS.timeGroups.yesterday,
			),
			last7Days: localize(
				translate,
				"sidebar.timeGroups.last7Days",
				SIDEBAR_LABELS.timeGroups.last7Days,
			),
			older: localize(
				translate,
				"sidebar.timeGroups.older",
				SIDEBAR_LABELS.timeGroups.older,
			),
		},
	};
}
